import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatKickoff } from "@/lib/api";
import TeamBadge from "@/components/app/TeamBadge";
import FDRPill from "@/components/app/FDRPill";
import { T } from "@/lib/testIds";

export default function Fixtures() {
  const [weeks, setWeeks] = useState(5);

  const { data: fdr } = useQuery({
    queryKey: ["fdr", weeks],
    queryFn: async () => (await api.get("/fixtures/fdr", { params: { weeks } })).data,
    staleTime: 5 * 60_000,
  });

  return (
    <div data-testid={T.fixturesPage} className="space-y-4 pt-1 animate-fade-up">
      <div className="flex items-center justify-between">
        <div className="label-caps">Fixture Difficulty (next {weeks})</div>
        <select
          data-testid={T.fdrWeeksSelect}
          value={weeks}
          onChange={(e) => setWeeks(Number(e.target.value))}
          className="bg-totem-surfaceElev border border-totem-border rounded-lg px-3 py-1.5 mono text-xs"
        >
          {[3, 5, 8, 10].map((w) => (
            <option key={w} value={w}>{w} GW</option>
          ))}
        </select>
      </div>

      <div data-testid={T.fdrGrid} className="totem-card p-3">
        {!fdr && <div className="h-40 animate-pulse" />}
        {fdr && (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-2 py-2 label-caps">Team</th>
                  {Array.from({ length: weeks }).map((_, i) => (
                    <th key={i} className="px-1 py-2 label-caps text-center">
                      GW{fdr.from_gw + i}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fdr.rows.map((row) => (
                  <tr key={row.team_id} className="border-t border-totem-borderSoft">
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2">
                        <TeamBadge teamCode={row.team_code} teamShort={row.team_short} size={20} />
                        <span className="mono uppercase text-xs">{row.team_short}</span>
                      </div>
                    </td>
                    {Array.from({ length: weeks }).map((_, i) => {
                      const gw = fdr.from_gw + i;
                      const fs = row.fixtures.filter((f) => f.gw === gw);
                      return (
                        <td key={i} className="px-1 py-1 text-center">
                          {fs.length === 0 ? (
                            <span className="mono text-xs text-totem-textFaint">-</span>
                          ) : (
                            <div className="flex flex-col gap-0.5 items-center">
                              {fs.map((f, idx) => (
                                <FDRPill
                                  key={idx}
                                  difficulty={f.difficulty}
                                  label={`${f.opponent_short}${f.is_home ? "(H)" : "(A)"}`}
                                />
                              ))}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UpcomingList />
    </div>
  );
}

function UpcomingList() {
  const { data } = useQuery({
    queryKey: ["fixtures-upcoming"],
    queryFn: async () => (await api.get("/fixtures")).data,
    staleTime: 5 * 60_000,
  });
  if (!data) return null;
  const grouped = {};
  data.results.forEach((f) => {
    const k = f.event || "TBC";
    grouped[k] = grouped[k] || [];
    grouped[k].push(f);
  });
  const gws = Object.keys(grouped).sort((a, b) => (a === "TBC" ? 1 : b === "TBC" ? -1 : a - b)).slice(0, 4);
  return (
    <div className="space-y-3">
      {gws.map((gw) => (
        <div key={gw} className="totem-card p-4">
          <div className="label-caps mb-3">Gameweek {gw}</div>
          <div className="space-y-2">
            {grouped[gw].map((f) => (
              <div key={f.id} className="flex items-center justify-between py-2 border-b border-totem-borderSoft last:border-0">
                <span className="mono text-[11px] text-totem-textDim w-24">
                  {formatKickoff(f.kickoff_time)}
                </span>
                <div className="flex items-center gap-2 flex-1 justify-center">
                  <span className="mono font-semibold">{f.team_h}</span>
                  <span className="text-totem-textFaint text-xs">v</span>
                  <span className="mono font-semibold">{f.team_a}</span>
                </div>
                <div className="flex gap-1">
                  <FDRPill difficulty={f.team_h_difficulty} label={String(f.team_h_difficulty ?? "-")} />
                  <FDRPill difficulty={f.team_a_difficulty} label={String(f.team_a_difficulty ?? "-")} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
